import React, { useEffect, useState, type FC, type Ref } from "react";

import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import MaskedInput from "react-text-mask";
import { toast } from "sonner";

import { Button, Card, Field, Input, Switch } from "@/components/ui";
import { phoneNumberMask, phoneRegExp, useSocialCoachSession } from "@/utils";
import { Coach } from "@/services/bedrock/dtos/Coaches";
import { CoachesServices } from "@/services/bedrock";

const validationProfileSchema = z.object({
  company: z.string().optional(),
  contactPhone: z
    .string()
    .refine(val => !val || phoneRegExp.test(val), "Contact Phone number is not valid.")
    .optional(),
  contactEmail: z
    .string()
    .refine(val => !val || z.string().email().safeParse(val).success, "Contact email is not valid.")
    .optional(),
  directSupportToCoach: z.boolean().optional(),
});

type CoachProfileFormValues = z.infer<typeof validationProfileSchema>;

const CoachProfile: FC = () => {
  const { coachId, refreshSession } = useSocialCoachSession();
  const [coach, setCoach] = useState<Coach>();

  const form = useForm<CoachProfileFormValues>({
    resolver: zodResolver(validationProfileSchema),
    mode: "onChange",
    defaultValues: {
      company: "",
      contactPhone: "",
      contactEmail: "",
      directSupportToCoach: false,
    },
  });

  const {
    handleSubmit,
    control,
    reset,
    trigger,
    formState: { isSubmitting, isValid },
  } = form;

  useEffect(() => {
    if (coachId) {
      CoachesServices.getCoach(coachId).then(setCoach);
    }
  }, [coachId]);

  useEffect(() => {
    if (coach) {
      reset({
        company: coach.companyName ?? "",
        contactPhone: coach.contactPhone ?? "",
        contactEmail: coach.contactEmail ?? "",
        directSupportToCoach: coach.directSupportToCoach ?? false,
      });
      trigger();
    }
  }, [coach, reset, trigger]);

  const onSubmit = async (data: CoachProfileFormValues) => {
    try {
      if (!coach) return;

      await CoachesServices.updateCoach(coach.id, {
        companyName: data.company,
        contactEmail: data.contactEmail,
        contactPhone: data.contactPhone,
        directSupportToCoach: data.directSupportToCoach,
      });

      toast.success("Company information updated successfully");
      refreshSession();
    } catch {
      toast.error("An error occurred while updating the profile");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col items-start max-w-[1300px] min-w-3xl ml-[10px]">
      <div className="flex flex-col gap-5 w-3xl">
        <div className="flex flex-col gap-4">
          <Card>
            <Card.Header>
              <Card.Title className="text-lg">Company Information</Card.Title>
              <Card.Description>
                This information will be displayed to Players within the SocialCoach app
              </Card.Description>
            </Card.Header>
            <Card.Content className="grid grid-cols-2 gap-4">
              <Controller
                name="company"
                control={control}
                render={({ field, fieldState }) => (
                  <Field label="Company Name" name={field.name} error={fieldState.error?.message}>
                    {controlProps => (
                      <Input
                        {...controlProps}
                        {...field}
                        autoComplete="section-company organization"
                        placeholder="Company"
                        disabled={isSubmitting}
                      />
                    )}
                  </Field>
                )}
              />
              <Controller
                name="contactEmail"
                control={control}
                render={({ field, fieldState }) => (
                  <Field label="Contact Email" name={field.name} error={fieldState.error?.message}>
                    {controlProps => (
                      <Input
                        {...controlProps}
                        {...field}
                        autoComplete="work email"
                        placeholder="Email"
                        disabled={isSubmitting}
                      />
                    )}
                  </Field>
                )}
              />
              <Controller
                name="contactPhone"
                control={control}
                render={({ field, fieldState }) => (
                  <Field label="Company Phone Number" name={field.name} error={fieldState.error?.message}>
                    {controlProps => (
                      <MaskedInput
                        {...controlProps}
                        {...field}
                        mask={phoneNumberMask}
                        render={(ref, props2) => (
                          <Input
                            ref={ref as Ref<HTMLInputElement>}
                            {...props2}
                            autoComplete="work tel"
                            placeholder="Phone Number"
                            disabled={isSubmitting}
                          />
                        )}
                      />
                    )}
                  </Field>
                )}
              />
              <Controller
                name="directSupportToCoach"
                control={control}
                render={({ field, fieldState }) => (
                  <Field label="Handle Player Support Requests" name={field.name} error={fieldState.error?.message}>
                    {controlProps => (
                      <Switch
                        {...controlProps}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={isSubmitting}
                      />
                    )}
                  </Field>
                )}
              />
            </Card.Content>
          </Card>

          <div className="flex flex-col items-start gap-2">
            <Button size="lg" type="submit" disabled={isSubmitting || !isValid} className="w-50">
              Save
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default CoachProfile;
